import {AttrsSchema, Component} from 'panel';
import {generate as generatePassword} from 'generate-password';
import copy from 'clipboard-copy';

import {publicDashboardLinksClient} from 'common/report/queries/app-api/public-dashboard-links-client';
import {addToast} from 'common/components/toast-area/toast-manager';
import {ToastType} from 'common/components/toast/types';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';
import {emitTrackEvent} from 'common/util/dom';
import {confirmDelete} from 'common/components/confirm';
import {sharingWidget} from 'common/widgets/sharing';
import {THEME as ConfirmTheme} from 'common/components/confirm/types';
import {SharingModalViewType} from 'common/widgets/sharing/modal/types';
import type {SharingModal} from 'common/widgets/sharing/modal';

import {template} from './template';
import css from './index.inline.styl';
import {PublicDashboardPasswordRowAttrs, PublicDashboardPasswordRowState} from './types';

const PASSWORD_SETTINGS = {
  length: 10,
  numbers: true,
  symbols: true,
  strict: true,
};

const EVENT_PREFIX = `Public Boards`;

const events = {
  passwordUpdated: `Public Board Password Updated`,
};

const properties = {
  passwordEnabled: `Public Board Password Enabled`,
};

export class PublicDashboardPasswordRow extends Component<
  PublicDashboardPasswordRowState,
  PublicDashboardPasswordRowAttrs
> {
  static get attrsSchema(): AttrsSchema<PublicDashboardPasswordRowAttrs> {
    return {
      ...super.attrsSchema,
      'password-protected': `boolean`,
      'workspace-id': `string`,
      'public-link-id': `string`,
      'public-link-embed-link-with-passcode': `string`,
    };
  }
  get config() {
    return {
      css,
      useShadowDom: true,
      template,
      defaultState: {
        editingPassword: false,
        password: ``,
        removePasswordLoading: false,
        setPasswordLoading: false,
      },
      helpers: {
        generatePassword: () => generatePassword(PASSWORD_SETTINGS),
        copyEmbedLink: async (embedLink: string) => {
          try {
            await copy(embedLink);
            addToast({
              message: `Embed URL copied!`,
              type: ToastType.SUCCESS,
            });
          } catch (err) {
            addToast({
              message: `Embed URL ${embedLink}`,
              type: ToastType.INFO,
            });
          }
        },
        handleCheckboxClick: () => {
          if (this.attr(`password-protected`)) {
            this.helpers.removePassword();
          } else {
            this.update({editingPassword: !this.state.editingPassword, password: this.helpers.generatePassword()});
          }
        },
        handleSaveClick: (ev) => {
          ev.stopPropagation();
          this.helpers.setPassword(this.state.password);
        },
        handleEditClick: () => this.update({editingPassword: true}),
        handlePasswordInput: (ev) => this.update({password: ev.target.value}),
        emitPasswordChangeEvent: (passwordProtected: boolean) =>
          this.dispatchEvent(
            new CustomEvent(`passwordChange`, {
              bubbles: true,
              detail: {passwordProtected, publicLinkId: this.attr(`public-link-id`)},
            }),
          ),
        getShareModalInfo: () => {
          // If a share modal is open, we extract the attributes so we can reopen the share modal after the
          // confirm modal.
          const isShareModalOpen = document.querySelector(`mp-sharing-modal`) ? true : false;
          const shareModalEl = isShareModalOpen ? document.querySelector<SharingModal>(`mp-sharing-modal`) : null;
          return {
            shareModalOpen: isShareModalOpen,
            shareModalAttrs: isShareModalOpen ? shareModalEl.attrs() : null,
          };
        },
        setPassword: async (password) => {
          if (!password) {
            return;
          }

          let confirmPasswordReset = true;
          const {shareModalOpen, shareModalAttrs} = this.helpers.getShareModalInfo();
          // when a user resets the password, we show confirm modal
          if (this.attr(`password-protected`)) {
            confirmPasswordReset = await confirmDelete({
              title: `Reset Passcode`,
              description: `Are you sure you want to reset the passcode?`,
              theme: ConfirmTheme.CONFIRM,
              'confirm-button-label': `Reset`,
            });
          }

          if (confirmPasswordReset) {
            this.update({setPasswordLoading: true});
            try {
              await publicDashboardLinksClient.setPublicDashboardLinkPassword({
                linkId: this.attr(`public-link-id`),
                workspaceId: this.attr(`workspace-id`),
                password,
              });
              addToast({message: `Public Board Passcode Saved`, type: ToastType.SUCCESS});
              this.track({eventName: events.passwordUpdated, props: {[properties.passwordEnabled]: true}});
              this.helpers.emitPasswordChangeEvent(true);
            } catch (err) {
              if ((err.message as string).includes(`commonly used or insecure passwords`)) {
                addToast({
                  message: `The password cannot be used because it is on our commonly used/insecure list`,
                  type: ToastType.ERROR,
                });
              } else {
                addToast({message: `Something went wrong saving password`, type: ToastType.ERROR});
              }
            }
          }
          this.update({setPasswordLoading: false, editingPassword: false, password: ``});

          // only re-open the share modal if a user is resetting the password
          const openPasswordRowItem = {
            PublicLinkId: this.attr(`public-link-id`),
            LastSavedPassword: password,
          };

          if (shareModalOpen) {
            sharingWidget.promptSharing({
              ...shareModalAttrs,
              'open-as': SharingModalViewType.PublicDashboardDetails,
              'open-password-row-item': openPasswordRowItem,
            });
          } else {
            this.update({setPasswordLoading: false, editingPassword: false});
          }
        },
        removePassword: async () => {
          this.update({removePasswordLoading: true});

          const {shareModalOpen, shareModalAttrs} = this.helpers.getShareModalInfo();
          const confirmPasswordRemove = await confirmDelete({
            title: `Remove Passcode`,
            description: `Are you sure you want to remove the passcode?`,
            theme: ConfirmTheme.CONFIRM,
            'confirm-button-label': `Remove`,
          });

          if (confirmPasswordRemove) {
            try {
              await publicDashboardLinksClient.deletePublicDashboardLinkPassword({
                linkId: this.attr(`public-link-id`),
                workspaceId: this.attr(`workspace-id`),
              });
              addToast({message: `Public Board Passcode Removed`, type: ToastType.SUCCESS});
              this.track({eventName: events.passwordUpdated, props: {[properties.passwordEnabled]: false}});
              this.helpers.emitPasswordChangeEvent(false);
            } catch (err) {
              addToast({message: `Something went wrong removing password`, type: ToastType.ERROR});
            }
          }

          if (shareModalOpen) {
            const openPasswordRowItem = {
              PublicLinkId: this.attr(`public-link-id`),
              LastSavedPassword: null,
            };
            sharingWidget.promptSharing({
              ...shareModalAttrs,
              'open-as': SharingModalViewType.PublicDashboardDetails,
              'open-password-row-item': openPasswordRowItem,
            });
          } else {
            this.update({removePasswordLoading: false, editingPassword: false});
          }
        },
      },
    };
  }

  track({eventName, props}) {
    emitTrackEvent(this, {
      eventName,
      eventPrefix: EVENT_PREFIX,
      props: {
        ...props,
      },
    });
  }
}

export const PublicDashboardPasswordRowEl = defineAndMakeJsxFactory(
  `mp-public-dashboard-password-row`,
  PublicDashboardPasswordRow,
);
export const PublicDashboardPasswordRowReact = panel2react(
  `mp-public-dashboard-password-row`,
  PublicDashboardPasswordRow,
);
